import React, { useState, useEffect, useRef } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import Table from "../../Components/Tenants/TableForExpiring";
import { toast } from "react-toastify";
import {
  BoxTitle,
  BoxContainer,
  BoxHead,
  Underline,
  FilterTableWrapper,
  FilterWrapper,
} from "../../Components/Styles/PageStyles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import SummaryCard from "../../Components/Tenants/SummaryCardForExpiring";
import NotFoundModel from "../../Components/NotFoundModel";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import AgreementRenew from "../../Components/Tenants/AgreementRenew";
import SideModal from "../../Components/SideModal";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import SearchFilterBox from "../../Components/SearchFilterBox";
import DateRange from "../../Components/DateRange";
import RenewedAgreementTable from "../../Components/Tenants/RenewedAgreementTable";
import ViewAgreementDetails from "../../Components/Tenants/ViewAgreementDetails";
import Notes from "../../Components/Tenants/Notes";

const TenantsAgreement = () => {
  const searchOptions = [
    { label: "Mobile", value: 1 },
    { label: "Name", value: 2 },
  ];
  const [previousStartDate, setPreviousStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [previousEndDate, setPreviousEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );

  const [startDate, setStartDate] = useState(previousStartDate);
  const [endDate, setEndDate] = useState(previousEndDate);
  const [filterType, setFilterType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const user = useSelector((state) => state.UserReducer.user);
  const property = useSelector(
    (state) => state.SelectPropertyReducer.selectProperty
  );
  const properties = useSelector((state) => state.PropertyReducer.property);
  const [selectedProperty, setSelectedProperty] = useState(property);

  const [isFocused, setIsFocused] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [selectedData, setSelectedData] = useState("");
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [filter, setFilter] = useState("");
  const [apiData, setApiData] = useState();
  const [masterData, setMasterData] = useState();
  const [isRenewAgreement, setIsRenewAgreement] = useState(false);
  const [isViewAgreement, setIsViewAgreement] = useState(false);
  const [isViewNotes, setIsViewNotes] = useState(false);

  const [summaryData, setSummaryData] = useState({
    totalMovedOut: 0,
    curMonthMovedOut: 0,
    defaulters: 0,
    totalDefaultedDues: 0,
  });

  const isApiCallInProgress = useRef(false);
  const lastApiCallTime = useRef(0);

  const { t, i18n } = useTranslation();

  const buildTab1Query = () => {
    return `propId=${selectedProperty?.id}&searchVal=&filter=${filter}&platform=3`;
  };

  const buildTab2Query = () => {

    return `&startDate=${startDate}&endDate=${endDate}&s=${searchQuery}&t=${filterType}`;
  };

  const fetchFunction = async () => {
    if (isApiCallInProgress.current) {
      return;
    }

    isApiCallInProgress.current = true;
    lastApiCallTime.current = Date.now();

    let url = "";

    if (activeTab === "1") {
      url = `${Config.apiUrl}/tenant/web/agreement-expiring?${buildTab1Query()}`;
    } else if (activeTab === "2") {
      url = `${Config.apiUrl}/tenant/list/renewed/agreements?${buildTab2Query()}`;
    }

    try {
      const response = await axios.get(url, {
        headers: {
          authorization: "Bearer" + " " + user.token,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
      });
      return response.data;
    } finally {
      setTimeout(() => {
        isApiCallInProgress.current = false;
      }, 300);
    }
  };

  const getSuccess = (res) => {
    if (res.isSuccess === true) {
      setApiData(res?.list || []);

      if (activeTab === "1") {
        setSummaryData(res?.stats || {
          totalMovedOut: 0,
          curMonthMovedOut: 0,
          defaulters: 0,
          totalDefaultedDues: 0,
        });
        setMasterData(res?.list || []);
      }

      setIsFocused(true);
    } else {
    }
  };

  const getError = (res) => {
    isApiCallInProgress.current = false;
  };

  const {
    isLoading: isLoading,
    mutate: getListMutate,
    error,
    data,
    isRefetching,
    isFetching,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
    onError: getError,
  });

  useEffect(() => {
    if (isFirstTime) return;

    const shouldCallApi = () => {
      if (
        isApiCallInProgress.current ||
        Date.now() - lastApiCallTime.current < 300 ||
        isFetching ||
        isRefetching ||
        isLoading
      ) {
        return false;
      }
      return true;
    };

    if (shouldCallApi()) {
      const handler = setTimeout(() => {
        getListMutate();
      }, 100);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    activeTab,
    filter,
    selectedProperty,
    startDate,
    endDate,
    searchQuery,
    filterType
  ]);

  useEffect(() => {
    if (isFirstTime) {
      const handler = setTimeout(() => {
        getListMutate();
        setIsFirstTime(false);
      }, 100);
      return () => {
        clearTimeout(handler);
      };
    }
  }, [property]);


  useEffect(() => {
    if (!isFirstTime) {
      setApiData([]);
      setMasterData([]);
      const handler = setTimeout(() => {
        if (!isApiCallInProgress.current) {
          getListMutate();
        }
      }, 150);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [activeTab]);


  const onSummaryBoxClick = (data) => {
    if (activeTab !== "1") return;

    sessionStorage.removeItem("tenant_expiring_page");
    let filteredData;
    const currentDate = moment().startOf("day");

    if (data === "ECM") {
      const startOfMonth = currentDate.clone().startOf("month");
      const endOfMonth = currentDate.clone().endOf("month");

      filteredData = masterData.filter((tenantData) => {
        if (!tenantData.agreementExpiry) return false;

        const expiryDate = moment(tenantData.agreementExpiry);
        return expiryDate.isBetween(startOfMonth, endOfMonth, null, "[]");
      });
    } else if (data === "ENM") {
      const startOfNextMonth = currentDate
        .clone()
        .add(1, "months")
        .startOf("month");
      const endOfNextMonth = currentDate
        .clone()
        .add(1, "months")
        .endOf("month");

      filteredData = masterData.filter((tenantData) => {
        if (!tenantData.agreementExpiry) return false;

        const expiryDate = moment(tenantData.agreementExpiry);
        return expiryDate.isBetween(
          startOfNextMonth,
          endOfNextMonth,
          null,
          "[]"
        );
      });
    } else if (data === "E") {
      filteredData = masterData.filter((tenantData) => {
        if (!tenantData.agreementExpiry) return false;

        const expiryDate = moment(tenantData.agreementExpiry);
        return expiryDate.isBefore(currentDate);
      });
    } else {
      filteredData = masterData;
    }
    setApiData(filteredData);
  };

  //------- Renew Agreement -------
  const renewAgreementFunction = async (values) => {
    return await axios.post(`${Config.apiUrl}/client/agreement/renew`, values, {
      headers: {
        authorization: "Bearer" + " " + user.token,
        "Content-Type": "application/json",
      },
    });
  };

  const onRenewAgreementSuccess = (data) => {
    if (data?.data?.isSuccess === true) {
      toast.success(data?.data?.msg || "Success");
      setIsRenewAgreement(false);
      getListMutate();
    } else {
      toast.error(data?.data?.msg || "An Error Occured");
    }
  };

  const onRenewAgreementError = (data) => {
    toast.error(data?.response?.data?.msg || "An Error Occured");
  };

  const { isLoading: renewAgreementLoading, mutate: renewAgreementMutate } =
    useMutation(renewAgreementFunction, {
      onSuccess: onRenewAgreementSuccess,
      onError: onRenewAgreementError,
    });

  const handleClose = () => {
    setIsRenewAgreement(false);
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
  };

  const handleFilterChange = (values) => {
    setFilterType(values.filterType);
    setSearchQuery(values.searchQuery);
  };

  const handleClearFilters = () => {
    if (activeTab === "1") {
      setFilter("");
    } else {
      setFilterType("");
      setSearchQuery("");
      setStartDate(previousStartDate);
      setEndDate(previousEndDate);
    }
  };

  const getDateRangeDisplayText = () => {
    if (
      startDate &&
      endDate &&
      moment(startDate).isValid() &&
      moment(endDate).isValid()
    ) {
      return (
        moment(startDate).format("DD MMM yyyy") +
        " - " +
        moment(endDate).format("DD MMM yyyy")
      );
    }
    return "Select Date Range";
  };

  const submitHandler = (values) => {
    const newValues = {
      startDate: moment(values.startDate).format("YYYY-MM-DD"),
      endDate: moment(values.endDate).format("YYYY-MM-DD"),
    };
    setStartDate(newValues.startDate);
    setEndDate(newValues.endDate);
    setPreviousStartDate(newValues.startDate);
    setPreviousEndDate(newValues.endDate);
  };

  const handleTabChange = (tab) => {
    isApiCallInProgress.current = false;
    setActiveTab(tab);
    setFilterType("");
    setSearchQuery("");
    setStartDate(previousStartDate);
    setEndDate(previousEndDate);
    setFilter("")
  };

  return (
    <>
      <BoxContainer>
        <BoxHead>
          <BoxTitle className="!mb-0 whitespace-nowrap">
            Agreements
            <Underline />
          </BoxTitle>
        </BoxHead>

        <SideModal
          isOpen={isRenewAgreement}
          onClose={() => setIsRenewAgreement(false)}
        >
          <AgreementRenew
            renewAgreementMutate={renewAgreementMutate}
            isRenewAgreement={isRenewAgreement}
            renewAgreementLoading={renewAgreementLoading}
            setIsRenewAgreement={setIsRenewAgreement}
            handleClose={handleClose}
            selectedData={selectedData}
          />
        </SideModal>

        <SideModal
          isOpen={isViewNotes}
          onClose={() => setIsViewNotes(false)}
        >
          <Notes
            isViewNotes={isViewNotes}
            setIsViewNotes={setIsViewNotes}
            selectedData={selectedData}
          />
        </SideModal>


        <SideModal isOpen={isViewAgreement} onClose={() => setIsViewAgreement(false)}>
          <ViewAgreementDetails
            setIsViewAgreement={setIsViewAgreement}
            selectedData={selectedData}

          />
        </SideModal>
        <FloorTabs>
          <FloorTab
            key="expiring"
            onClick={() => handleTabChange('1')}
            $active={activeTab === "1"}
          >
            Expiring Agreement
          </FloorTab>
          <FloorTab
            key="valid"
            onClick={() => handleTabChange('2')}
            $active={activeTab === "2"}
          >
            Renewed Agreement
          </FloorTab>
        </FloorTabs>

        {activeTab === "1" && (
          <>
            {(isLoading || isRefetching) && !isFocused && (
              <SummarySkeletonLoading count={3} />
            )}
            {isFocused && apiData && (
              <SummaryCard summaryData={summaryData} onClick={onSummaryBoxClick} />
            )}

            {!error && !isLoading && apiData && (
              <FilterTableWrapper>
                <FilterWrapper className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left: Total Record */}
                  <div className="flex items-center">
                    <Text>
                      Total records:{" "}
                      <span className="font-semibold">
                        {apiData?.length || 0}
                      </span>
                    </Text>
                  </div>
                  <div className="flex gap-2 justify-end items-center">
                    <PropertiesFilterDropdown
                      properties={properties}
                      selectedProperty={selectedProperty}
                      onPropertySelect={handlePropertySelect}
                      placeholder="Select Property"
                      className="min-w-[200px] w-full md:w-[220px]"
                    />
                  </div>
                </FilterWrapper>
                <TableWrapper tab="1">
                  <Table
                    apiData={error ? [] : apiData?.length > 0 ? apiData : []}
                    setSelectedData={setSelectedData}
                    selectedData={selectedData}
                    setIsRenewAgreement={setIsRenewAgreement}
                    setIsViewNotes={setIsViewNotes}
                  />
                  {(error || apiData.length === 0) && !isLoading && <NotFoundModel msg={"All good! No leases are expiring."} />}
                </TableWrapper>
              </FilterTableWrapper>
            )}
          </>
        )}

        {activeTab === "2" && (
          <>
            <FilterTableWrapper>
              <FilterWrapper className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left: Total Record */}
                <div className="flex items-center">
                  <Text>
                    Total records:{" "}
                    <span className="font-semibold">
                      {apiData?.length || 0}
                    </span>
                  </Text>
                </div>
                {/* <StatusBadge>
                  <Text>
                    Renewed Agreements {data?.count || 0}
                  </Text>
                </StatusBadge> */}
                {/* <SearchFilterBox
                    options={searchOptions}
                    handleFilterChange={handleFilterChange}
                    handleClearFilters={handleClearFilters}
                  // className="flex-1 min-w-0"
                  /> */}
                <div className="flex gap-2 justify-end items-center">
                  <DateRange
                    submitHandler={submitHandler}
                    defaultLabel={getDateRangeDisplayText()}
                    className="flex-1 min-w-0"
                  />
                </div>
              </FilterWrapper>
              {!error && !isLoading && apiData && (
                <TableWrapper tab="2">
                  <RenewedAgreementTable
                    apiData={error ? [] : apiData?.length > 0 ? apiData : []}
                    setSelectedData={setSelectedData}
                    selectedData={selectedData}
                    setIsViewAgreement={setIsViewAgreement}
                  />
                  {(error || apiData.length === 0) && !isLoading && <NotFoundModel msg={"No valid agreements found."} />}
                </TableWrapper>
              )}
            </FilterTableWrapper>

          </>
        )}

        {(isLoading || isRefetching) && <Loading />}
        {error && !isLoading && <NotFoundModel />}
      </BoxContainer>
    </>
  );
};


const TableWrapper = tw.h1`
  bg-white rounded-md 
  ${props => props.tab === "1" ? "mt-4" : "mt-4"}
`;
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-4 border-b`;
const FloorTab = tw.button`
  ${(p) =>
    p.$active
      ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
      : " text-gray-700 "} 
   px-5 py-3  cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;
// const StatusBadge = tw.div`bg-blue-100 px-4 py-2 items-center rounded-br-lg lg:mb-20 xl:mb-6 mb-2`;
const StatusBadge = tw.div`bg-blue-100 px-4 py-2 items-center rounded-br-lg  xl:mb-6 mb-2`;
//const Text = tw.p`text-xs text-blue-500 font-semibold `;
const Text = tw.p`text-gray-500 text-sm font-normal `;

export default TenantsAgreement;